import React from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Filter, Eye, Plus, Trash2, Palette, X } from 'lucide-react';
import MapWorld from './MapWorld/MapWorld';

export default function CartographieModals({
  showLegendModal, setShowLegendModal, newLegend, setNewLegend, addCustomLegend,
  customLegends, toggleLegend, removeLegend,
  showFilterModal, setShowFilterModal, newFilter, setNewFilter, addCustomFilter,
  customFilters, toggleFilter, removeFilter,
  isFullscreen, setIsFullscreen, getAuthHeader,
}) {
  return (
    <>
      {/* Modal Gestion Légendes */}
      <Dialog open={showLegendModal} onOpenChange={setShowLegendModal}>
        <DialogContent className="max-w-lg bg-slate-900 border-blue-500/30 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-purple-400" />
              Personnaliser la Légende
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-800/50 rounded-lg border border-blue-500/20">
              <Label className="text-blue-300 mb-2 block">Nouvelle légende</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={newLegend.color}
                  onChange={(e) => setNewLegend({...newLegend, color: e.target.value})}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <Input
                  placeholder="Libellé de la légende"
                  value={newLegend.label}
                  onChange={(e) => setNewLegend({...newLegend, label: e.target.value})}
                  className="flex-1 bg-slate-700 border-blue-500/30"
                />
                <Button onClick={addCustomLegend} className="bg-green-700 hover:bg-green-600">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {customLegends.map((legend) => (
                <div
                  key={legend.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                    legend.active
                      ? 'bg-slate-800/50 border-blue-500/30'
                      : 'bg-slate-900/50 border-slate-700 opacity-50'
                  }`}
                >
                  <div
                    className="w-6 h-6 rounded-full border-2 border-white/30"
                    style={{ backgroundColor: legend.color }}
                  ></div>
                  <span className="flex-1 text-white">{legend.label}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleLegend(legend.id)}
                    className={legend.active ? 'text-green-400' : 'text-gray-400'}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeLegend(legend.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLegendModal(false)} className="border-slate-600">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Gestion Filtres */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="max-w-lg bg-slate-900 border-blue-500/30 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-green-400" />
              Gérer les Filtres Personnalisés
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-800/50 rounded-lg border border-blue-500/20">
              <Label className="text-blue-300 mb-2 block">Nouveau filtre</Label>
              <div className="space-y-2">
                <Input
                  placeholder="Nom du filtre"
                  value={newFilter.name}
                  onChange={(e) => setNewFilter({...newFilter, name: e.target.value})}
                  className="bg-slate-700 border-blue-500/30"
                />
                <div className="flex gap-2">
                  <Input
                    placeholder="Critères (ex: priority:high, date:week)"
                    value={newFilter.criteria}
                    onChange={(e) => setNewFilter({...newFilter, criteria: e.target.value})}
                    className="flex-1 bg-slate-700 border-blue-500/30"
                  />
                  <Button onClick={addCustomFilter} className="bg-green-700 hover:bg-green-600">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {customFilters.map((filter) => (
                <div
                  key={filter.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                    filter.active
                      ? 'bg-green-900/30 border-green-500/30'
                      : 'bg-slate-800/50 border-slate-700'
                  }`}
                >
                  <div className="flex-1">
                    <p className="font-medium text-white">{filter.name}</p>
                    <p className="text-xs text-slate-400 font-mono">{filter.criteria}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleFilter(filter.id)}
                    className={filter.active ? 'text-green-400' : 'text-gray-400'}
                  >
                    {filter.active ? 'Actif' : 'Inactif'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeFilter(filter.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFilterModal(false)} className="border-slate-600">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Plein Écran */}
      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full bg-slate-900 border-blue-500/30 p-0">
          <div className="relative w-full h-full">
            <div className="absolute top-4 right-4 z-50">
              <Button
                onClick={() => setIsFullscreen(false)}
                className="bg-slate-800/80 hover:bg-slate-700"
              >
                <X className="h-4 w-4 mr-2" />
                Fermer
              </Button>
            </div>
            <div className="w-full h-full">
              <MapWorld isAdminView={true} getAuthHeader={getAuthHeader} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
